<script setup>
import { computed, ref } from "vue";
import ExpertCard from "@/Components/Brand/sections/ExpertCard.vue";
import { useInertiaAction } from "@/composables/useInertiaAction.js";

const props = defineProps({
    lists: {
        type: Array,
        default: () => [],
    },
    // Category tabs. Empty while categories are parked for launch
    // (GH-1252): the section then shows every list it is given, untabbed.
    tabs: {
        type: Array,
        default: () => [],
    },
});

const emit = defineEmits(["bookmark-pending"]);

// The number of lists shown under the "Featured" (first) tab.
const FEATURED_COUNT = 3;

const hasTabs = computed(() => props.tabs.length > 0);
const activeTab = ref(props.tabs[0] ?? null);

// Red downward triangle used as the mobile select's only dropdown indicator
// (the native arrow is removed via appearance-none).
const selectCaret = `url("data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20width='14'%20height='10'%20viewBox='0%200%2014%2010'%3E%3Cpath%20fill='%23f21439'%20d='M7%2010%200%200h14z'/%3E%3C/svg%3E")`;

// "Featured" shows the most recent lists; the rest filter by the category map
// (config/dashboard.php) keyed off the lowercased tab label.
const visibleLists = computed(() => {
    if (!hasTabs.value) {
        return props.lists;
    }

    if (activeTab.value === props.tabs[0]) {
        return props.lists.slice(0, FEATURED_COUNT);
    }

    const slug = activeTab.value.toLowerCase();

    return props.lists.filter((list) => list.category === slug);
});

// --- Bookmark toggle (GH-994): same optimistic pattern as Explore, plus a
//     per-card busy uuid so the chip throbs while its request runs. Busy
//     spans the follow-up reload too (GH-1159), so the sidebar's Saved Lists
//     and the header badge have caught up before the chip settles.
const { busy: bookmarkBusy, run: runBookmark } = useInertiaAction();
const bookmarkBusyUuid = ref(null);

async function toggleBookmark(list) {
    if (bookmarkBusy.value) {
        return;
    }

    bookmarkBusyUuid.value = list.uuid;
    // Lets the sidebar show the list as pending (ghost row / throbbing
    // trash) until the reload lands.
    emit("bookmark-pending", {
        uuid: list.uuid,
        name: list.title,
        image: list.image,
        url: list.url,
        adding: !list.is_bookmark,
    });

    await runBookmark(
        () =>
            list.is_bookmark
                ? axios.delete(
                      route("bookmark.destroy", { giftlist: list.uuid }),
                  )
                : axios.post(route("bookmark.create", { giftlist: list.uuid })),
        {
            only: ["savedListsCount", "savedLists"],
            onSuccess: () => {
                list.is_bookmark = !list.is_bookmark;
            },
        },
    );

    bookmarkBusyUuid.value = null;
    emit("bookmark-pending", null);
}
</script>

<template>
    <section>
        <header class="mb-6 flex items-center justify-between gap-3">
            <h2 class="font-brand text-[25px] font-bold text-[#102a3d]">
                Expert Lists
            </h2>

            <!-- Mobile: tabs collapse to a category dropdown. -->
            <div v-if="hasTabs" class="relative sm:hidden">
                <select
                    v-model="activeTab"
                    :style="{ backgroundImage: selectCaret }"
                    class="appearance-none rounded-brand border border-[#dad9d2] bg-white bg-[position:right_1rem_center] bg-[length:12px_9px] bg-no-repeat py-2.5 pl-4 pr-10 font-brand text-[15px] font-semibold text-brand-navy [-moz-appearance:none] [-webkit-appearance:none]"
                >
                    <option v-for="tab in tabs" :key="tab" :value="tab">
                        {{ tab }}
                    </option>
                </select>
            </div>

            <!-- Desktop: inline tabs. -->
            <ul
                v-if="hasTabs"
                class="flex flex-wrap items-center gap-x-10 gap-y-2 font-brand text-[15px] font-semibold max-sm:hidden"
            >
                <li v-for="tab in tabs" :key="tab">
                    <button
                        type="button"
                        class="pb-1 transition"
                        :class="
                            activeTab === tab
                                ? 'text-[#102a3d] shadow-[0_2px_0_0_#f21439]'
                                : 'text-[#102a3d]/50 hover:text-[#102a3d]'
                        "
                        @click="activeTab = tab"
                    >
                        {{ tab }}
                    </button>
                </li>
            </ul>

            <!-- With tabs, the mobile select takes this slot instead. -->
            <a
                :href="route('explore')"
                class="inline shrink-0 font-brand text-[15px] font-medium text-[#102a3d] underline underline-offset-2 transition-colors hover:text-brand-red"
                :class="{ 'max-sm:hidden': hasTabs }"
            >
                Explore All
            </a>
        </header>

        <!--
            Cards: a horizontal snap-carousel on mobile (cards peek to hint that
            more exist), switching to a grid from sm up.
        -->
        <div
            v-if="visibleLists.length"
            class="-mx-4 flex snap-x scroll-px-4 gap-4 overflow-x-auto px-4 pb-2 [-ms-overflow-style:none] [scrollbar-width:none] sm:mx-0 sm:grid sm:grid-cols-2 sm:gap-5 sm:overflow-visible sm:px-0 sm:pb-0 sm:scroll-px-0 xl:grid-cols-3 [&::-webkit-scrollbar]:hidden"
        >
            <div
                v-for="list in visibleLists"
                :key="list.uuid"
                class="w-[84%] shrink-0 snap-start sm:w-auto sm:shrink"
            >
                <ExpertCard
                    :creator="list.creator"
                    :title="list.title"
                    :description="list.description"
                    :image="list.image"
                    :avatar="list.avatar"
                    :href="list.url"
                    :bookmarked="list.is_bookmark"
                    :busy="bookmarkBusyUuid === list.uuid"
                    bordered
                    expert
                    :masthead-photo="list.masthead_photo"
                    :masthead-focus-x="list.masthead_focus_x"
                    :masthead-focus-y="list.masthead_focus_y"
                    @bookmark="toggleBookmark(list)"
                />
            </div>
        </div>

        <p
            v-else
            class="rounded-brand border border-dashed border-brand-tan/70 px-4 py-10 text-center font-brand text-[15px] text-[#565a77]"
        >
            {{
                hasTabs
                    ? "No expert lists in this category yet."
                    : "No expert lists yet."
            }}
        </p>
    </section>
</template>
